import React from "react";
// Import UI components for styling and tooltips
import { OverlayTrigger, Tooltip } from "react-bootstrap"; 
import { Info, Filter, Eye, X } from "lucide-react"; 
import { FaSort, FaSortUp, FaSortDown, FaEye } from "react-icons/fa";

import Pagination from "../../components/Pagination/Pagination";
import {
  formatCurrency,
  formatDateTime,
  mapStatusToBootstrap,
} from "../../components/Constants/Constants";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../components/AppLoader/appLoader";
// import UnAuthorized from "../UnAuthorized/UnAuthorized";
// import usePermission from "../../utils/usePermission";
import { useMiscSettlement } from "../../customHooks/MiscSettlement/useMiscSettlement";
import CopyIcon from "../../components/CopyIcon/CopyIcon";

// Define column information for Misc Settlement Tooltips
const miscSettlementColumnInfo = {
    settlementId: "Unique identifier for the miscellaneous settlement record.",
    settlementTxnId: "The unique transaction ID related to this settlement.",
    providerId: "The ID of the Seller/Logistics Provider.",
    providerName: "The name of the Seller/Logistics Provider.",
    providerAmount: "The amount recorded by the Provider for the settlement.",
    providerStatus: "The settlement status from the Provider's perspective.",
    selfAmount: "The amount calculated by the self system/BPP for the settlement.",
    selfStatus: "The settlement status from the self system/BPP's perspective.",
    createdAt: "The date and time the settlement record was created.",
};

const MiscSettlement = () => {
  const today = new Date().toISOString().slice(0, 10);
  const {
    settlements,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    sortConfig,
    handleSort,
    paginatedSettlements,
    totalPages,
    currentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    itemsPerPageOptions,
    handleItemsPerPageChange,
    filterOptions, // Include dynamic filters
    selectedOption,
    setSelectedOption,
    searchValue,
    setSearchValue,
    handleDropdownChange,
    handleSearchChange,
    searchOptions,
  } = useMiscSettlement();

  //   const canViewSettlements = usePermission("SETTLEMENT_LISTING_VIEW");

  const navigate = useNavigate();
  return (
    <>
      {/* {canViewSettlements ? ( */}
      <div className="m-4">
        <p className="text-muted small mb-3">
            This page provides a consolidated view of miscellaneous settlement records.
            Use the filters below to search for specific records and click on a Settlement ID
            to view detailed information.
        </p>

        {/* --- Filters (Styled like Orders) --- */}
        <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm">
          <div className="row gx-4 gy-3 align-items-end flex-wrap">
            {/* Search Section - Styled like Generic Search in Orders */}
            <div className="col-auto">
              <label className="form-label mb-1">
                Search By
              </label>
              <div className="input-group" style={{ minWidth: "400px" }}>
                <select
                  className="form-select"
                  value={selectedOption}
                  onChange={handleDropdownChange}
                  style={{ maxWidth: "150px" }}
                >
                  {searchOptions?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder={`Enter ${selectedOption
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}`}
                  style={{
                    fontSize: "12px",
                    padding: "none",
                  }}
                />
              </div>
            </div>

            {/* Provider Status */}
            <div className="col-auto">
              <label className="form-label mb-1">
                Provider Status
              </label>
              <select
                className="form-select"
                value={filters.provider_status || ""}
                onChange={(e) =>
                  handleFilterChange("provider_status", e.target.value)
                }
                style={{ minWidth: "140px" }}
              >
                <option value="">All</option>
                {filterOptions?.providerStatuses?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Self Status */}
            <div className="col-auto">
              <label className="form-label mb-1">
                Self Status
              </label>
              <select
                className="form-select"
                value={filters.self_status || ""}
                onChange={(e) =>
                  handleFilterChange("self_status", e.target.value)
                }
                style={{ minWidth: "140px" }}
              >
                <option value="">All</option>
                {filterOptions?.selfStatuses?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Settlement Status */}
            <div className="col-auto">
              <label className="form-label mb-1">
                Settlement Status
              </label>
              <select
                className="form-select"
                value={filters.settlement_status || ""}
                onChange={(e) =>
                  handleFilterChange("settlement_status", e.target.value)
                }
                style={{ minWidth: "120px" }}
              >
                <option value="">All</option>
                {filterOptions?.settlementStatuses?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Created From (Date Range start) - Harmonized styling */}
            <div className="col-auto">
              <label className="form-label mb-1">Created From</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.settlement_created_at_from}
                  onChange={(e) =>
                    handleFilterChange(
                      "settlement_created_at_from",
                      e.target.value
                    )
                  }
                  max={today}
                  style={{ fontSize: "12px", padding: "none" }}
                  title="From Date"
                />
            </div>

            {/* Created To (Date Range end) - Harmonized styling */}
            <div className="col-auto">
              <label className="form-label mb-1">Created To</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.settlement_created_at_to}
                  onChange={(e) =>
                    handleFilterChange(
                      "settlement_created_at_to",
                      e.target.value
                    )
                  }
                  max={today}
                  min={filters.settlement_created_at_from || ""}
                  style={{ fontSize: "12px", padding: "none" }}
                  title="To Date"
                />
            </div>

            {/* Clear Filters Button - Styled like Orders Clear Filters button */}
            <div className="col-auto d-flex">
              <button
                type="button"
                className="btn btn-primary no-hover-btn"
                style={{ padding: "5px 8px" }}
                onClick={clearFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Orders table */}
        {loading ? (
          <AppLoader />
        ) : paginatedSettlements && paginatedSettlements.length > 0 ? (
          <>
            <div
              className="table-responsive mt-4"
              style={{ overflowX: "auto" }}
            >
              <table
                className="table table-bordered table-hover align-middle text-center"
              >
                <thead className="table-light text-center">
                  <tr>
                    {/* Settlement ID */}
                    <th>
                        Settlement ID
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.settlementId}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>
                    
                    {/* Settlement Txn. ID */}
                    <th>
                        Settlement Txn. ID
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.settlementTxnId}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Provider ID */}
                    <th>
                        Provider ID
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.providerId}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>
                    
                    {/* Provider Name */}
                    <th>
                        Provider Name
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.providerName}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Provider Amount */}
                    <th>
                        Provider Amount
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.providerAmount}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Provider Status */}
                    <th>
                        Provider Status
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.providerStatus}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>
                    
                    {/* Self Amount */}
                    <th>
                        Self Amount
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.selfAmount}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>
                    
                    {/* Self Status */}
                    <th>
                        Self Status
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{miscSettlementColumnInfo.selfStatus}</Tooltip>}
                        >
                            <span style={{ marginLeft: "4px", cursor: "help" }}>
                                <Info size={14} className="text-muted" />
                            </span>
                        </OverlayTrigger>
                    </th>

                    {/* Created At - Sorting enabled */}
                    <th
                      onClick={() => handleSort("createdAt")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Created At &nbsp;
                      {sortConfig.key === "createdAt" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{miscSettlementColumnInfo.createdAt}</Tooltip>}
                    >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                            <Info size={14} className="text-muted" />
                        </span>
                    </OverlayTrigger>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {paginatedSettlements.map((settlement, index) => (
                      <tr key={`${settlement.settlement_id}-${index}`}>
                        {/* Settlement ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span
                            onClick={() =>
                              navigate(
                                `/misc-settlements-detail/${settlement.settlement_id}`
                              )
                            }
                            style={{
                              cursor: "pointer",
                              color: "#0d6efd",
                              fontWeight: 500,
                              textDecoration: "none",
                            }}
                            title="View Settlements Details"
                            className="truncate-cell"
                          >
                            {settlement.settlement_id}
                          </span>
                          <CopyIcon textToCopy={settlement.settlement_id} />{" "}
                        </td>

                        {/* Settlement Txn. ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {settlement.settlement_txn_id}
                          </span>
                          <CopyIcon textToCopy={settlement.settlement_txn_id} />
                        </td>

                        {/* Provider ID - Removed minWidth */}
                        <td>
                          {settlement?.order?.provider.id}
                        </td>

                        {/* Provider Name - Removed minWidth */}
                        <td>
                          {settlement?.order?.provider?.name}
                        </td>

                        {/* Provider Amount - Removed minWidth */}
                        <td>
                          {formatCurrency(
                            settlement?.order?.provider?.amount?.value
                          )}
                        </td>

                        {/* Provider Status - Removed minWidth */}
                        <td>
                          {settlement?.order?.provider?.status}
                        </td>

                        {/* Self Amount - Removed minWidth */}
                        <td>
                          {formatCurrency(
                            settlement?.order?.self?.amount?.value
                          )}
                        </td>

                        {/* Self Status - Removed minWidth */}
                        <td>
                          {settlement?.order?.self?.status}
                        </td>

                        {/* Created At - Removed minWidth */}
                        <td>
                          {formatDateTime(settlement.createdAt)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#9ca3af",
              fontSize: "14px",
              marginTop: "16px",
            }}
          >
            No Settlements Found
          </div>
        )}

        {/* Pagination */}
        {totalOrderCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            startItem={startItem}
            endItem={endItem}
            totalItems={totalOrderCount}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onPageChange={goToPage}
            itemsPerPageOptions={itemsPerPageOptions}
          />
        )}
      </div>
      {/* //   ) : (
      //     <UnAuthorized />
      //   )} */}
    </>
  );
};

export default MiscSettlement;